import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useNavigationType } from "react-router-dom";
import { Button, Dialog, IconButton } from "@mui/material";
import { mdiCheck, mdiClose, mdiMagnify, mdiMapMarkerOutline } from "@mdi/js";
import Icon from "@mdi/react";
import GoogleMapReact from "google-map-react";

import AppBar from "components/AppBar";
import FAB from "components/FAB";
import SlideUpTransition from "components/SlideUpTransition";
import { EventLocation } from "api/event";
import { googleMapsApiKey } from "utils/env";
import styles from "./locationPickerDialog.module.scss";

interface GeolocationI {
  location: {
    lat: number;
    lng: number;
  };
  accuracy: number;
}

export interface LocationPickerDialogProps {
  onSaveLocation: (location: EventLocation | null) => void;
  location: EventLocation | null;
}

function LocationPickerDialog(props: LocationPickerDialogProps) {
  const { onSaveLocation, location } = props;
  const mounted = useRef<boolean>(false);
  const [searchValue, setSearchValue] = useState("");
  const [selectedLocation, setSelectedLocation] = useState<EventLocation | null>(null);
  const [defaultCenter, setDefaultCenter] = useState<GeolocationI["location"] | null>(null);
  const [mapsApi, setMapsApi] = useState<any>(null);
  const [map, setMap] = useState<any>(null);
  const [isListening, setIsListening] = useState(false);
  const [open, setOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const navigate = useNavigate();
  const routerLocation = useLocation();
  const navigationType = useNavigationType();

  /**
   * Open pick location dialog
   */
  const handleOpenPickLocation = () => {
    navigate(routerLocation.pathname);
    setOpen(true);
  };

  /**
   * Handle confirm selected location
   */
  const handleConfirmLocation = () => {
    onSaveLocation(selectedLocation);
    navigate(-1);
  };

  /**
   * Handle clear location search
   */
  const handleClear = () => {
    if (searchRef.current) {
      searchRef.current.focus();
    }
    setSearchValue("");
    setSelectedLocation(null);
  };

  /**
   * Get user approximate location
   */
  const getUserLocation = async () => {
    const res = await fetch(`https://www.googleapis.com/geolocation/v1/geolocate?key=${googleMapsApiKey}`, {
      method: "POST",
    });
    const data = (await res.json()) as GeolocationI;
    if (data && mounted.current) {
      setDefaultCenter(data.location);
    }
  };

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  });

  /**
   * Get user location and focus input
   */
  useEffect(() => {
    if (!defaultCenter) {
      getUserLocation();
    }

    if (open && location) {
      setSearchValue(location.name);
    }
  }, [open, defaultCenter, location]);

  useEffect(() => {
    let autocomplete: any;
    if (mapsApi && map && !isListening && defaultCenter) {
      autocomplete = new mapsApi.places.Autocomplete(searchRef.current);
      const marker = new mapsApi.Marker({ map });
      setIsListening(true);

      // Check if location is already set
      if (location) {
        map.setCenter(location.coordinates);
        marker.setPosition(location.coordinates);
        setSearchValue(location.name);
      } else {
        map.setCenter(defaultCenter);
      }

      autocomplete.addListener("place_changed", () => {
        const place = autocomplete.getPlace();

        if (place.geometry) {
          marker.setPosition(place.geometry.location);
          map.setCenter(place.geometry.location);
          setSelectedLocation({
            coordinates: {
              lat: place.geometry.location.lat(),
              lng: place.geometry.location.lng(),
            },
            name: place.formatted_address,
          });
          setSearchValue(place.formatted_address);
        }
      });
    }
  }, [map, mapsApi, isListening, defaultCenter, location]);

  /**
   * On pressing back button close opened dialogs
   */
  useEffect(() => {
    if (navigationType === "POP") {
      setOpen(false);
    }
  }, [navigationType]);

  return (
    <>
      <Button
        fullWidth
        onClick={handleOpenPickLocation}
        size='large'
        startIcon={<Icon path={mdiMapMarkerOutline} size={1} />}
        variant='lowercaseSimple'
      >
        {location ? location.name : "Select location"}
      </Button>

      <Dialog keepMounted fullScreen open={open} TransitionComponent={SlideUpTransition}>
        <AppBar noRadius title='Select location' />

        <div className={styles.searchInputContainer}>
          <Icon className={styles.searchInputIcon} path={mdiMagnify} size={1} />
          <label className='sr-only' htmlFor='search-location'>
            Search location
          </label>
          <input
            className={styles.searchInput}
            id='search-location'
            onChange={(e) => setSearchValue(e.target.value)}
            placeholder='Search location'
            ref={searchRef}
            type='text'
            value={searchValue}
          />
          <IconButton aria-label='Clear search' className={styles.clearInputButton} onClick={handleClear}>
            <Icon className={styles.clearInputIcon} path={mdiClose} size={0.8} />
          </IconButton>
        </div>

        <div className={styles.mapContainer}>
          <GoogleMapReact
            bootstrapURLKeys={{
              key: googleMapsApiKey,
              libraries: ["places"],
            }}
            defaultCenter={{
              lat: 59.95,
              lng: 30.33,
            }}
            defaultZoom={11}
            onGoogleApiLoaded={({ map: googleMap, maps }) => {
              setMap(googleMap);
              setMapsApi(maps);
            }}
            yesIWantToUseGoogleMapApiInternals
          />
        </div>
        <FAB className={styles.fab} icon={mdiCheck} title='Save location' onClick={handleConfirmLocation} />
      </Dialog>
    </>
  );
}

export default LocationPickerDialog;
